/**
 * CAD ⇄ SceneObject projection (Phase A1).
 *
 * The drafting model (state/store.Entity) is preserved as-is; these
 * functions project entities into the shared protocol and merge
 * protocol-level edits back. CAD is the reference citizen: its visible /
 * locked / layer fields already match the protocol one-to-one.
 */

import type { Entity } from '../../state/store';
import type { SceneKind, SceneObject } from './types';

/** CAD entity type string → shared semantic kind. */
const CAD_TYPE_TO_KIND: Record<string, SceneKind> = {
    line: 'line',
    polyline: 'polyline',
    rectangle: 'polyline',
    polygon: 'polyline',
    circle: 'circle',
    arc: 'arc',
    ellipse: 'ellipse',
    curve: 'curve',
    'nurbs-curve': 'curve',
    'nurbs-surface': 'surface',
    'nurbs-polysurf': 'surface',
    solid: 'mesh',
    surface: 'surface',
    subd: 'subd',
    mesh: 'mesh',
    insert: 'block',
    block: 'block',
    leader: 'annotation',
    attdef: 'annotation',
    hatch: 'hatch',
    table: 'annotation',
    text: 'text',
    mtext: 'text',
    dimension: 'dimension',
    point: 'point',
    // Underlays & scan data (Faz 9.7/9.9)
    image: 'image',
    pointcloud: 'pointcloud',
    box: 'mesh',
    sphere: 'mesh',
    cylinder: 'mesh',
    loft: 'surface',
    extrude: 'surface',
    revolve: 'surface',
    // 3D drafting primitives that mirror BIM categories
    wall: 'wall',
    door: 'door',
    window: 'window',
    floor: 'floor',
    column: 'column',
    roof: 'roof',
    ceiling: 'ceiling',
    stair: 'stair',
    railing: 'railing',
    ramp: 'railing',
    component: 'generic',
    area: 'room',
    toposolid: 'mass',
};

export function cadTypeToKind(type: string): SceneKind {
    return CAD_TYPE_TO_KIND[type.toLowerCase()] ?? 'generic';
}

/** Project one CAD entity into the shared protocol. */
export function cadEntityToSceneObject(entity: Entity): SceneObject {
    const position = Array.isArray(entity.data?.position)
        ? [...entity.data.position]
        : [0, 0, 0];
    const rotation = Array.isArray(entity.data?.rotation)
        ? [...entity.data.rotation]
        : [0, 0, 0];
    return {
        id: entity.id,
        source: 'cad',
        kind: cadTypeToKind(entity.type),
        displayName: entity.name || entity.type,
        layerId: entity.layer,
        visible: entity.visible,
        locked: entity.locked,
        placement: {
            position: position as SceneObject['placement']['position'],
            rotation: rotation as SceneObject['placement']['rotation'],
            scale: [1, 1, 1],
        },
        data: { ...entity.data, entityType: entity.type },
    };
}

/** Merge protocol-level edits back into the owning CAD entity shape. */
export function applySceneObjectToCadEntity(
    entity: Entity,
    obj: SceneObject,
): Entity {
    return {
        ...entity,
        visible: obj.visible,
        locked: obj.locked,
        layer: obj.layerId,
        name: obj.displayName,
    };
}

/**
 * Inverse projection: rebuild a live CAD entity from its protocol record.
 * Used when a loaded document's `cadScene` is rehydrated into the CAD
 * workbench store (see utils/ProjectFileIO).
 *
 * Persistence-fidelity fix (docs/plans/2d plan F5): a drafting entity's
 * OWN `data` bag already carries the authoritative position/rotation fields
 * (text/table anchor, ellipse's numeric major-axis angle, arc spans). The
 * old code unconditionally overwrote `data.position`/`data.rotation` with the
 * protocol placement — which flattened ellipse rotation (a number) to
 * [0,0,0]. The protocol placement is now only a FALLBACK for payloads that
 * store no position/rotation of their own.
 */
export function sceneObjectToCadEntity(obj: SceneObject): Entity {
    const { entityType, ...data } = obj.data as Record<string, unknown> & {
        entityType?: string;
    };
    const out: Record<string, unknown> = { ...data };
    if (!Array.isArray(out.position)) out.position = obj.placement.position;
    // Ellipse/arc carry a numeric rotation; keep it when present.
    if (out.rotation === undefined || out.rotation === null) {
        out.rotation = obj.placement.rotation;
    }
    return {
        id: obj.id,
        type: typeof entityType === 'string' ? entityType : 'line',
        name: obj.displayName,
        visible: obj.visible,
        locked: obj.locked,
        layer: obj.layerId,
        data: out,
    };
}
